import { useState } from "react";
import { previewPricebookItems } from "../../../../services";
import {
  buildItemInputs,
  buildPreviewRequest,
  countRemovedLinesWithPeriods,
} from "./lineEdits";
import { PriceChangeRow, buildPriceChangeRows } from "./priceChangeRows";
import { savePricebookPrices } from "./savePricebookPrices";
import { PricebookEditor } from "./usePricebookEditor";

const MAX_LISTED_INVALID_LINES = 5;

const INVALID_BOOK_DISCOUNT =
  "กรุณากรอกส่วนลดทั้งสมุดเป็นตัวเลข 0–100 ทศนิยมไม่เกิน 2 ตำแหน่ง";
const PREVIEW_FAILED =
  "คำนวณราคาก่อนบันทึกไม่สำเร็จ ยังไม่มีการเปลี่ยนแปลงใดถูกบันทึก กรุณาลองอีกครั้ง";

/** Names the lines the API would refuse, including ones on another page of the table. */
const buildInvalidLinesMessage = (codes: string[]): string => {
  const listed = codes.slice(0, MAX_LISTED_INVALID_LINES).join(", ");
  const remaining = codes.length - MAX_LISTED_INVALID_LINES;
  const suffix = remaining > 0 ? ` และอีก ${remaining} รายการ` : "";
  return `กรุณากรอกราคาต่อหน่วยและส่วนลด % ให้ถูกต้อง (${listed}${suffix})`;
};

/** The book % before and after the save, shown above the changed lines. */
export interface BookDiscountChange {
  before: number;
  after: number;
}

interface PricebookPriceSaveOptions {
  pricebookUuid: string;
  storedBookDiscount: number;
  editor: PricebookEditor;
  /** Something was written, so the header (book %, counts, editor) has to be read again. */
  onSaved: () => void;
}

/**
 * บันทึก on the unsaved bar: validate, let the server price every line without writing
 * (POST /items/preview), confirm the changes, then write. The prices in the confirmation are
 * the server's, so what is confirmed is what is stored.
 */
export function usePricebookPriceSave({
  pricebookUuid,
  storedBookDiscount,
  editor,
  onSaved,
}: PricebookPriceSaveOptions) {
  const [changeRows, setChangeRows] = useState<PriceChangeRow[] | null>(null);
  const [isPreviewing, setIsPreviewing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const newBookDiscount = editor.isBookDiscountChanged
    ? Number(editor.bookDiscountText)
    : null;
  const bookDiscountChange: BookDiscountChange | null =
    newBookDiscount === null
      ? null
      : { before: storedBookDiscount, after: newBookDiscount };
  // Only the open confirmation shows it; nothing can be edited while it is open.
  const removedPeriodLineCount =
    changeRows === null
      ? 0
      : countRemovedLinesWithPeriods(editor.storedLines, editor.lines);

  const requestSave = async () => {
    setErrorMessage("");
    // Sending a line set that never loaded would take every product off the book.
    if (editor.loading || editor.loadFailed) return;
    if (!editor.isBookDiscountValid) {
      setErrorMessage(INVALID_BOOK_DISCOUNT);
      return;
    }
    const invalidCodes = editor.flagInvalidLines();
    if (invalidCodes.length > 0) {
      setErrorMessage(buildInvalidLinesMessage(invalidCodes));
      return;
    }
    setIsPreviewing(true);
    try {
      const {
        data: { data },
      } = await previewPricebookItems(
        pricebookUuid,
        buildPreviewRequest(editor.lines, newBookDiscount),
      );
      setChangeRows(
        buildPriceChangeRows(editor.storedLines, editor.lines, data?.items ?? []),
      );
    } catch {
      // The axios interceptor shows the API's reason; this says nothing was written.
      setErrorMessage(PREVIEW_FAILED);
    } finally {
      setIsPreviewing(false);
    }
  };

  const cancelConfirm = () => setChangeRows(null);

  const confirmSave = async () => {
    setIsSaving(true);
    const outcome = await savePricebookPrices({
      pricebookUuid,
      bookDiscountPercent: newBookDiscount,
      items: buildItemInputs(editor.lines),
    });
    setIsSaving(false);
    setChangeRows(null);
    if (outcome.errorMessage) {
      setErrorMessage(outcome.errorMessage);
      if (outcome.isBookDiscountSaved) onSaved();
      return;
    }
    editor.reload();
    onSaved();
  };

  const dismissError = () => setErrorMessage("");

  return {
    changeRows,
    bookDiscountChange,
    removedPeriodLineCount,
    isPreviewing,
    isSaving,
    errorMessage,
    requestSave,
    cancelConfirm,
    confirmSave,
    dismissError,
  };
}
